import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  LogOut, Users, CalendarCheck, CreditCard, GitBranch, Inbox, LayoutDashboard,
  Plus, Pencil, Trash2, X, History, Search,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const TABS = [
  { k: "overview", l: "Resumen", icon: LayoutDashboard },
  { k: "users", l: "Usuarios", icon: Users },
  { k: "reservations", l: "Reservas", icon: CalendarCheck },
  { k: "payments", l: "Pagos", icon: CreditCard },
  { k: "referrals", l: "Referidos", icon: GitBranch },
  { k: "applications", l: "Solicitudes", icon: Inbox },
  { k: "activity", l: "Actividad", icon: History },
];

const eur = (n) => `${(n ?? 0).toLocaleString("es-ES")} €`;

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <header className="backdrop-blur-xl bg-black/60 border-b border-white/10 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <span className="font-serif-display text-2xl cursor-pointer" onClick={() => navigate("/")}>
            Sol<span className="text-gold">Club</span>
            <span className="ml-3 text-xs tracking-luxe-sm uppercase text-white/40 align-middle">Admin</span>
          </span>
          <div className="flex items-center gap-6">
            <span className="text-white/50 font-light text-sm hidden md:inline">{user?.email}</span>
            <button data-testid="logout-btn" onClick={async () => { await logout(); navigate("/"); }}
              className="flex items-center gap-2 text-xs tracking-luxe-sm uppercase text-white/70 hover:text-gold transition-colors">
              <LogOut size={16} strokeWidth={1.5} /> Salir
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        <p className="text-xs tracking-luxe uppercase text-gold mb-4">Panel de Administración</p>
        <h1 className="font-serif-display text-4xl md:text-5xl mb-10">Control total</h1>

        <div className="flex flex-wrap gap-2 mb-12 border-b border-white/10">
          {TABS.map((t) => (
            <button
              key={t.k}
              data-testid={`admin-tab-${t.k}`}
              onClick={() => setTab(t.k)}
              className={`flex items-center gap-2 px-5 py-3 text-xs tracking-luxe-sm uppercase border-b-2 -mb-px transition-colors ${
                tab === t.k ? "border-gold text-gold" : "border-transparent text-white/50 hover:text-white/80"
              }`}
            >
              <t.icon size={15} strokeWidth={1.5} /> {t.l}
            </button>
          ))}
        </div>

        {tab === "overview" && <Overview />}
        {tab === "users" && <UsersTab />}
        {tab === "reservations" && <ReservationsTab />}
        {tab === "payments" && <PaymentsTab />}
        {tab === "referrals" && <ReferralsTab />}
        {tab === "applications" && <ApplicationsTab />}
        {tab === "activity" && <ActivityTab />}
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th className="text-left px-5 py-3 text-xs tracking-luxe-sm uppercase text-white/40 font-normal">{children}</th>;
}
function Td({ children, className = "" }) {
  return <td className={`px-5 py-4 font-light text-sm ${className}`}>{children}</td>;
}

function Toolbar({ query, setQuery, filters }) {
  return (
    <div className="flex flex-wrap items-center gap-3">
      <div className="relative">
        <Search size={15} strokeWidth={1.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
        <input data-testid="admin-search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Buscar…"
          className="bg-transparent border border-white/15 pl-9 pr-4 py-2.5 text-sm font-light text-white placeholder-white/30 focus:border-gold focus:outline-none w-56" />
      </div>
      {filters}
    </div>
  );
}

function Select({ value, onChange, options, testid }) {
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="bg-transparent border border-white/15 px-4 py-2.5 text-sm font-light text-white focus:border-gold focus:outline-none [&>option]:bg-[#121212]">
      {options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
    </select>
  );
}

/* ---------------- Overview ---------------- */
function Overview() {
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/stats").then(({ data }) => setStats(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, []);
  const cards = [
    { l: "Usuarios", v: stats?.users },
    { l: "Socios activos", v: stats?.active_members },
    { l: "Partners", v: stats?.partners },
    { l: "Referidos", v: stats?.referrals },
    { l: "Reservas", v: stats?.reservations },
    { l: "Solicitudes", v: stats?.applications },
    { l: "Transacciones", v: stats?.transactions },
    { l: "Ingresos", v: eur(stats?.revenue) },
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-5" data-testid="admin-overview">
      {cards.map((c) => (
        <div key={c.l} className="border border-white/10 p-8 hover:border-gold/40 transition-colors">
          <p className="font-serif-display text-3xl md:text-4xl mb-2 text-gold">{c.v ?? "—"}</p>
          <p className="text-white/50 font-light text-xs tracking-luxe-sm uppercase">{c.l}</p>
        </div>
      ))}
    </div>
  );
}

/* ---------------- Users ---------------- */
const EMPTY_USER = { name: "", email: "", password: "", role: "member", membership_status: "inactive" };

function UsersTab() {
  const [users, setUsers] = useState([]);
  const [modal, setModal] = useState(null); // {mode:'create'|'edit', data}
  const load = () => api.get("/admin/users").then(({ data }) => setUsers(data.users)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const [q, setQ] = useState("");
  const [roleF, setRoleF] = useState("all");
  const filtered = users.filter((u) =>
    (roleF === "all" || u.role === roleF) &&
    `${u.name} ${u.email}`.toLowerCase().includes(q.toLowerCase()));

  const del = async (u) => {
    if (!window.confirm(`¿Eliminar a ${u.email}?`)) return;
    try { await api.delete(`/admin/users/${u.id}`); toast.success("Usuario eliminado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-users">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <Toolbar query={q} setQuery={setQ} filters={
          <Select value={roleF} onChange={setRoleF} testid="filter-role"
            options={[["all", "Todos los roles"], ["member", "Miembro"], ["partner", "Partner"], ["admin", "Admin"]]} />
        } />
        <button data-testid="admin-add-user" onClick={() => setModal({ mode: "create", data: { ...EMPTY_USER } })}
          className="flex items-center gap-2 bg-gold text-black px-6 py-3 text-xs tracking-luxe-sm uppercase hover:bg-[#C5A059] transition-colors">
          <Plus size={16} strokeWidth={1.5} /> Alta usuario
        </button>
      </div>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full min-w-[720px]">
          <thead><tr className="border-b border-white/10">
            <Th>Nombre</Th><Th>Email</Th><Th>Rol</Th><Th>Membresía</Th><Th>Código</Th><Th>Acciones</Th>
          </tr></thead>
          <tbody>
            {filtered.map((u) => (
              <tr key={u.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`user-row-${u.id}`}>
                <Td className="text-white/80">{u.name}</Td>
                <Td className="text-white/60">{u.email}</Td>
                <Td><span className={`text-xs tracking-luxe-sm uppercase ${u.role === "admin" ? "text-gold" : "text-white/60"}`}>{u.role}</span></Td>
                <Td className="text-white/60">{u.membership_status}</Td>
                <Td className="text-white/40">{u.referral_code || "—"}</Td>
                <Td>
                  <div className="flex gap-3">
                    <button data-testid={`edit-user-${u.id}`} onClick={() => setModal({ mode: "edit", data: { id: u.id, name: u.name, email: u.email, role: u.role, membership_status: u.membership_status, password: "" } })}
                      className="text-white/60 hover:text-gold"><Pencil size={16} strokeWidth={1.5} /></button>
                    <button data-testid={`delete-user-${u.id}`} onClick={() => del(u)}
                      className="text-white/60 hover:text-red-400"><Trash2 size={16} strokeWidth={1.5} /></button>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {modal && <UserModal modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

const FIELD = "w-full bg-transparent border-b border-white/20 py-3 text-white placeholder-white/30 font-light focus:border-gold focus:outline-none transition-colors [&>option]:bg-[#121212]";

function UserModal({ modal, onClose, onSaved }) {
  const [form, setForm] = useState(modal.data);
  const [saving, setSaving] = useState(false);
  const isEdit = modal.mode === "edit";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEdit) {
        const payload = { name: form.name, email: form.email, role: form.role, membership_status: form.membership_status };
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/users/${form.id}`, payload);
      } else {
        await api.post("/admin/users", form);
      }
      toast.success(isEdit ? "Usuario actualizado" : "Usuario creado");
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? "Modificar usuario" : "Alta de usuario"} onClose={onClose} testid="user-modal">
      <form onSubmit={submit} className="space-y-6" data-testid="user-form">
        <input required data-testid="user-name" className={FIELD} placeholder="Nombre"
          value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <input required type="email" data-testid="user-email" className={FIELD} placeholder="Email"
          value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
        <input type="password" data-testid="user-password" className={FIELD}
          placeholder={isEdit ? "Nueva contraseña (opcional)" : "Contraseña (mín. 6)"}
          value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
        <select data-testid="user-role" className={FIELD} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })}>
          <option value="member">Miembro</option>
          <option value="partner">Partner</option>
          <option value="admin">Admin</option>
        </select>
        <select data-testid="user-membership" className={FIELD} value={form.membership_status} onChange={(e) => setForm({ ...form, membership_status: e.target.value })}>
          <option value="inactive">Membresía inactiva</option>
          <option value="active">Membresía activa</option>
        </select>
        <button type="submit" disabled={saving} data-testid="user-save"
          className="w-full bg-gold text-black py-4 text-xs tracking-luxe uppercase hover:bg-[#C5A059] transition-colors disabled:opacity-50">
          {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear usuario"}
        </button>
      </form>
    </Modal>
  );
}

/* ---------------- Reservations ---------------- */
const EMPTY_RES = { client_name: "", client_email: "", service_type: "Hotel & Villa", destination: "", start_date: "", end_date: "", value: "", status: "confirmada", notes: "" };

function ReservationsTab() {
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState(null);
  const load = () => api.get("/admin/reservations").then(({ data }) => setItems(data.reservations)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);

  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const filtered = items.filter((r) =>
    (statusF === "all" || r.status === statusF) &&
    `${r.client_name} ${r.destination || ""} ${r.service_type}`.toLowerCase().includes(q.toLowerCase()));

  const del = async (r) => {
    if (!window.confirm("¿Eliminar reserva?")) return;
    try { await api.delete(`/admin/reservations/${r.id}`); toast.success("Reserva eliminada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="admin-reservations">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <Toolbar query={q} setQuery={setQ} filters={
          <Select value={statusF} onChange={setStatusF} testid="filter-res-status"
            options={[["all", "Todos los estados"], ["confirmada", "Confirmada"], ["pendiente", "Pendiente"], ["en curso", "En curso"], ["completada", "Completada"], ["cancelada", "Cancelada"]]} />
        } />
        <button data-testid="admin-add-reservation" onClick={() => setModal({ mode: "create", data: { ...EMPTY_RES } })}
          className="flex items-center gap-2 bg-gold text-black px-6 py-3 text-xs tracking-luxe-sm uppercase hover:bg-[#C5A059] transition-colors">
          <Plus size={16} strokeWidth={1.5} /> Nueva reserva
        </button>
      </div>
      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full min-w-[820px]">
          <thead><tr className="border-b border-white/10">
            <Th>Cliente</Th><Th>Servicio</Th><Th>Destino</Th><Th>Fechas</Th><Th>Valor</Th><Th>Estado</Th><Th>Acciones</Th>
          </tr></thead>
          <tbody>
            {filtered.length ? filtered.map((r) => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`res-row-${r.id}`}>
                <Td className="text-white/80">{r.client_name}</Td>
                <Td className="text-white/60">{r.service_type}</Td>
                <Td className="text-white/60">{r.destination || "—"}</Td>
                <Td className="text-white/50">{[r.start_date, r.end_date].filter(Boolean).join(" → ") || "—"}</Td>
                <Td className="text-gold">{eur(r.value)}</Td>
                <Td className="text-white/60">{r.status}</Td>
                <Td>
                  <div className="flex gap-3">
                    <button data-testid={`edit-res-${r.id}`} onClick={() => setModal({ mode: "edit", data: { ...EMPTY_RES, ...r, value: r.value ?? "" } })} className="text-white/60 hover:text-gold"><Pencil size={16} strokeWidth={1.5} /></button>
                    <button data-testid={`delete-res-${r.id}`} onClick={() => del(r)} className="text-white/60 hover:text-red-400"><Trash2 size={16} strokeWidth={1.5} /></button>
                  </div>
                </Td>
              </tr>
            )) : <tr><Td className="text-white/40">No hay reservas todavía.</Td></tr>}
          </tbody>
        </table>
      </div>
      {modal && <ReservationModal modal={modal} onClose={() => setModal(null)} onSaved={() => { setModal(null); load(); }} />}
    </div>
  );
}

function ReservationModal({ modal, onClose, onSaved }) {
  const [form, setForm] = useState(modal.data);
  const [saving, setSaving] = useState(false);
  const isEdit = modal.mode === "edit";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      client_name: form.client_name, client_email: form.client_email || null,
      service_type: form.service_type, destination: form.destination || null,
      start_date: form.start_date || null, end_date: form.end_date || null,
      value: parseFloat(form.value) || 0, status: form.status, notes: form.notes || null,
    };
    try {
      if (isEdit) await api.patch(`/admin/reservations/${form.id}`, payload);
      else await api.post("/admin/reservations", payload);
      toast.success(isEdit ? "Reserva actualizada" : "Reserva creada");
      onSaved();
    } catch (e2) { toast.error(formatApiError(e2.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  return (
    <Modal title={isEdit ? "Modificar reserva" : "Nueva reserva"} onClose={onClose} testid="res-modal">
      <form onSubmit={submit} className="space-y-5" data-testid="res-form">
        <input required data-testid="res-client" className={FIELD} placeholder="Nombre del cliente"
          value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
        <input data-testid="res-client-email" className={FIELD} placeholder="Email del cliente (opcional)"
          value={form.client_email} onChange={(e) => setForm({ ...form, client_email: e.target.value })} />
        <select data-testid="res-service" className={FIELD} value={form.service_type} onChange={(e) => setForm({ ...form, service_type: e.target.value })}>
          {["Hotel & Villa", "Viaje Corporativo", "Aviación & Yate", "Experiencia & Celebración"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <input data-testid="res-destination" className={FIELD} placeholder="Destino"
          value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} />
        <div className="grid grid-cols-2 gap-5">
          <input type="date" data-testid="res-start" className={FIELD} value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
          <input type="date" data-testid="res-end" className={FIELD} value={form.end_date || ""} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
        </div>
        <input type="number" data-testid="res-value" className={FIELD} placeholder="Valor (EUR)"
          value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} />
        <select data-testid="res-status" className={FIELD} value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
          {["confirmada", "pendiente", "en curso", "completada", "cancelada"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <textarea rows={2} data-testid="res-notes" className={FIELD} placeholder="Notas (opcional)"
          value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
        <button type="submit" disabled={saving} data-testid="res-save"
          className="w-full bg-gold text-black py-4 text-xs tracking-luxe uppercase hover:bg-[#C5A059] transition-colors disabled:opacity-50">
          {saving ? "Guardando…" : isEdit ? "Guardar cambios" : "Crear reserva"}
        </button>
      </form>
    </Modal>
  );
}

/* ---------------- Payments ---------------- */
function PaymentsTab() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/payments").then(({ data }) => setItems(data.payments)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))); }, []);
  return (
    <div className="border border-white/10 overflow-x-auto" data-testid="admin-payments">
      <table className="w-full min-w-[760px]">
        <thead><tr className="border-b border-white/10">
          <Th>Usuario</Th><Th>Plan</Th><Th>Importe</Th><Th>Estado</Th><Th>Fecha</Th>
        </tr></thead>
        <tbody>
          {items.length ? items.map((p) => (
            <tr key={p.id} className="border-b border-white/5">
              <Td className="text-white/70">{p.user_email || "—"}</Td>
              <Td className="text-white/60">{p.lookup_key?.replace(/_/g, " ")}</Td>
              <Td className="text-gold">{p.amount?.toLocaleString("es-ES")} {p.currency?.toUpperCase()}</Td>
              <Td><span className={`text-xs tracking-luxe-sm uppercase ${p.payment_status === "paid" ? "text-gold" : "text-white/50"}`}>{p.payment_status}</span></Td>
              <Td className="text-white/40">{(p.created_at || "").slice(0, 10)}</Td>
            </tr>
          )) : <tr><Td className="text-white/40">No hay transacciones.</Td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- Referrals ---------------- */
function ReferralsTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/referrals").then(({ data }) => setItems(data.referrals)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const filtered = items.filter((r) =>
    (statusF === "all" || r.status === statusF) &&
    `${r.client_name} ${r.partner_email || ""} ${r.service_type}`.toLowerCase().includes(q.toLowerCase()));
  const setStatus = async (r, status) => {
    try { await api.patch(`/admin/referrals/${r.id}`, { status }); toast.success("Referido actualizado"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="border border-white/10 overflow-x-auto" data-testid="admin-referrals">
      <div className="flex flex-wrap items-center gap-4 p-5 border-b border-white/10">
        <Toolbar query={q} setQuery={setQ} filters={
          <Select value={statusF} onChange={setStatusF} testid="filter-ref-status"
            options={[["all", "Todos los estados"], ["pending", "Pendiente"], ["confirmed", "Confirmado"], ["cancelled", "Cancelado"]]} />
        } />
      </div>
      <table className="w-full min-w-[820px]">
        <thead><tr className="border-b border-white/10">
          <Th>Partner</Th><Th>Cliente</Th><Th>Servicio</Th><Th>Valor</Th><Th>Comisión</Th><Th>Estado</Th><Th>Acciones</Th>
        </tr></thead>
        <tbody>
          {filtered.length ? filtered.map((r) => (
            <tr key={r.id} className="border-b border-white/5" data-testid={`admin-ref-${r.id}`}>
              <Td className="text-white/60">{r.partner_email || "—"}</Td>
              <Td className="text-white/80">{r.client_name}</Td>
              <Td className="text-white/60">{r.service_type}</Td>
              <Td className="text-white/60">{eur(r.estimated_value)}</Td>
              <Td className="text-gold">{eur(r.commission)}</Td>
              <Td><span className={`text-xs tracking-luxe-sm uppercase ${r.status === "confirmed" ? "text-gold" : r.status === "cancelled" ? "text-red-400" : "text-white/50"}`}>{r.status}</span></Td>
              <Td>
                <div className="flex gap-2">
                  {r.status !== "confirmed" && <button data-testid={`confirm-ref-${r.id}`} onClick={() => setStatus(r, "confirmed")} className="text-xs tracking-luxe-sm uppercase text-gold hover:underline">Confirmar</button>}
                  {r.status !== "cancelled" && <button data-testid={`cancel-ref-${r.id}`} onClick={() => setStatus(r, "cancelled")} className="text-xs tracking-luxe-sm uppercase text-white/40 hover:text-red-400">Cancelar</button>}
                </div>
              </Td>
            </tr>
          )) : <tr><Td className="text-white/40">No hay referidos.</Td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- Applications ---------------- */
function ApplicationsTab() {
  const [items, setItems] = useState([]);
  const load = () => api.get("/admin/applications").then(({ data }) => setItems(data.applications)).catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  useEffect(() => { load(); }, []);
  const [q, setQ] = useState("");
  const [statusF, setStatusF] = useState("all");
  const filtered = items.filter((a) =>
    (statusF === "all" || a.status === statusF) &&
    `${a.full_name} ${a.email} ${a.interest}`.toLowerCase().includes(q.toLowerCase()));
  const setStatus = async (a, status) => {
    try { await api.patch(`/admin/applications/${a.id}`, { status }); toast.success("Solicitud actualizada"); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="border border-white/10 overflow-x-auto" data-testid="admin-applications">
      <div className="flex flex-wrap items-center gap-4 p-5 border-b border-white/10">
        <Toolbar query={q} setQuery={setQ} filters={
          <Select value={statusF} onChange={setStatusF} testid="filter-app-status"
            options={[["all", "Todos los estados"], ["new", "Nueva"], ["contacted", "Contactado"], ["closed", "Cerrada"]]} />
        } />
      </div>
      <table className="w-full min-w-[820px]">
        <thead><tr className="border-b border-white/10">
          <Th>Nombre</Th><Th>Email</Th><Th>Teléfono</Th><Th>Interés</Th><Th>Estado</Th><Th>Acciones</Th>
        </tr></thead>
        <tbody>
          {filtered.length ? filtered.map((a) => (
            <tr key={a.id} className="border-b border-white/5" data-testid={`admin-app-${a.id}`}>
              <Td className="text-white/80">{a.full_name}</Td>
              <Td className="text-white/60">{a.email}</Td>
              <Td className="text-white/60">{a.phone || "—"}</Td>
              <Td className="text-white/60">{a.interest}</Td>
              <Td><span className={`text-xs tracking-luxe-sm uppercase ${a.status === "contacted" ? "text-gold" : a.status === "closed" ? "text-white/30" : "text-white/60"}`}>{a.status}</span></Td>
              <Td>
                <div className="flex gap-3">
                  <button data-testid={`contact-app-${a.id}`} onClick={() => setStatus(a, "contacted")} className="text-xs tracking-luxe-sm uppercase text-gold hover:underline">Contactado</button>
                  <button data-testid={`close-app-${a.id}`} onClick={() => setStatus(a, "closed")} className="text-xs tracking-luxe-sm uppercase text-white/40 hover:text-white/70">Cerrar</button>
                </div>
              </Td>
            </tr>
          )) : <tr><Td className="text-white/40">No hay solicitudes.</Td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- Activity ---------------- */
function ActivityTab() {
  const [logs, setLogs] = useState([]);
  useEffect(() => {
    api.get("/admin/audit").then(({ data }) => setLogs(data.logs))
      .catch((e) => toast.error(formatApiError(e.response?.data?.detail)));
  }, []);
  return (
    <div className="border border-white/10 overflow-x-auto" data-testid="admin-activity">
      <table className="w-full min-w-[720px]">
        <thead><tr className="border-b border-white/10">
          <Th>Fecha</Th><Th>Admin</Th><Th>Acción</Th><Th>Entidad</Th><Th>Detalle</Th>
        </tr></thead>
        <tbody>
          {logs.length ? logs.map((l) => (
            <tr key={l.id} className="border-b border-white/5" data-testid={`audit-row-${l.id}`}>
              <Td className="text-white/40">{(l.created_at || "").replace("T", " ").slice(0, 16)}</Td>
              <Td className="text-white/70">{l.admin_email}</Td>
              <Td className="text-gold">{l.action}</Td>
              <Td className="text-white/60">{l.entity}</Td>
              <Td className="text-white/50">{l.detail || "—"}</Td>
            </tr>
          )) : <tr><Td className="text-white/40">Sin actividad registrada.</Td></tr>}
        </tbody>
      </table>
    </div>
  );
}

/* ---------------- Modal shell ---------------- */
function Modal({ title, children, onClose, testid }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm px-6 py-10 overflow-y-auto" data-testid={testid}>
      <div className="bg-[#0d0d0d] border border-white/15 w-full max-w-lg p-10 my-auto">
        <div className="flex items-center justify-between mb-8">
          <h3 className="font-serif-display text-2xl">{title}</h3>
          <button onClick={onClose} data-testid="close-modal" className="text-white/50 hover:text-white"><X strokeWidth={1.5} /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
